import React, {
  createContext,
  useState,
  useContext,
  useEffect,
  ReactNode,
  useRef,
} from "react";
import { callErrorToast } from "../../../utilities/utilities";
import { ErrorResponse } from "../../../Models/Error";
import {
  KpiShipmentStatistic,
  KpiShipmentStatisticFilter,
} from "../../../Models/KpiShipmentStatistic";
import { KpiService } from "../../../services/shipment/kpi.ts";

const kpiService = new KpiService();

interface ShipmentsStatisticsProviderProps {
  children: ReactNode;
  filters: KpiShipmentStatisticFilter;
}

interface ShipmentsStatisticsContextValue {
  data: KpiShipmentStatistic | null;
  loader: boolean;
  filters: KpiShipmentStatisticFilter;
}

const ShipmentsStatisticsContext = createContext<
  ShipmentsStatisticsContextValue | undefined
>(undefined);

const ShipmentsStatisticsProvider: React.FC<
  ShipmentsStatisticsProviderProps
> = ({ children, filters }) => {
  const [data, setData] = useState<KpiShipmentStatistic | null>(null);
  const [loader, setLoader] = useState<boolean>(false);
  const [error, setError] =
    useState<ErrorResponse<KpiShipmentStatistic> | null>(null);

  const prevFilters = useRef<KpiShipmentStatisticFilter>(filters);

  const getStatistics = (): void => {
    setLoader(true);
    kpiService
      .getStatistics(filters)
      .then((res) => {
        setLoader(false);
        setData(res?.data);
      })
      .catch((err) => {
        setLoader(false);
        setError(err);
      });

    return;
  };

  useEffect(() => {
    if (error) {
      callErrorToast(error);
    }
  }, [error]);

  useEffect(() => {
    if (
      filters &&
      JSON.stringify(prevFilters.current) !== JSON.stringify(filters)
    ) {
      getStatistics();
      prevFilters.current = filters;
    }
  }, [filters]);

  useEffect(() => {
    getStatistics();
  }, []);

  return (
    <ShipmentsStatisticsContext.Provider
      value={{
        data,
        loader,
        filters,
      }}
    >
      {children}
    </ShipmentsStatisticsContext.Provider>
  );
};

/**
 * Custom hook to access the ShipmentsStatisticsContext.
 * Ensures that the context is used only within a ShipmentsStatisticsProvider.
 */
const useShipmentsStatisticsContext = (): ShipmentsStatisticsContextValue => {
  const context = useContext(ShipmentsStatisticsContext);
  if (!context) {
    throw new Error(
      "useShipmentsStatisticsContext must be used within a ShipmentsStatisticsProvider"
    );
  }
  return context;
};

// Export the provider and custom hook for use in other components
export { ShipmentsStatisticsProvider, useShipmentsStatisticsContext };
